import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import { generateGlobalColumns, IDataGridConfig, TInheritedProps } from '@ts/storybook';
import {
  CAddressType,
  CChannelType,
  CCreateKPI,
  CDistributionConfiguration,
  CDistributionList,
  CEntity,
  CKPI,
  CRuleset,
  CTaskType,
  IConfigComponent,
  CGroupTemplate,
  CViewType,
  TEntity,
  CProcessCategory,
} from '@ts/ui-models';
import { CLayer } from '@models/maps/Layer';
import { postKPIDefinition } from '@services/DataBase/KPI.service';

interface IGlobalDataGridProps {
  configComponent: IConfigComponent;
  inheritedProps?: TInheritedProps<false>;
}

export const generateConfigComponent = (entity: TEntity): IConfigComponent => {
  switch (entity) {
    case 'appProfile':
      return {
        componentId: 'appProfile',
        aclId: 'taAppProfile',
        entity: 'appProfile',
        fetchURL: '/api/appProfiles',
        crudURL: '/api/appProfile',
      };
    case 'uniqueIdSerie':
      return {
        componentId: 'uniqueIdSeries',
        aclId: 'UniqueNumSerie',
        entity: 'uniqueIdSerie',
        fetchURL: '/api/uniqueIdSeries',
        crudURL: '/api/uniqueIdSerie',
      };
    case 'license':
      return {
        componentId: 'licenses',
        aclId: 'tsLicense',
        entity: 'license',
        fetchURL: '/api/licenses',
        crudURL: '/api/license',
      };
    case 'licenseType':
      return {
        componentId: 'licenseTypes',
        aclId: 'tsLicenseType',
        entity: 'licenseType',
        fetchURL: '/api/licenseTypes',
      };
    case 'processCategory':
      return {
        componentId: 'processCategories',
        aclId: 'ProcessCategory',
        entity: 'processCategory',
        fetchURL: '/api/v3/processCategory',
        mainClass: CProcessCategory,
      };
    case 'groupTemplate':
      return {
        componentId: 'groupTemplates',
        aclId: 'GroupTemplate',
        entity: 'groupTemplate',
        fetchURL: '/api/v3/groupTemplate',
        crudURL: '/api/v3/groupTemplate',
        mainClass: CGroupTemplate,
      };
    case 'identityProvider':
      return {
        componentId: 'identityProvider',
        aclId: 'identityProvider',
        entity: 'identityProvider',
        fetchURL: '/api/identityProviders',
      };
    case 'physicalQuantity':
      return {
        componentId: 'physicalQuantities',
        aclId: 'physicalQuantity',
        entity: 'physicalQuantity',
        fetchURL: '/api/physicalQuantities',
      };
    case 'viewType':
      return {
        componentId: 'default',
        aclId: 'taViewType',
        entity: 'viewType',
        fetchURL: '/api/viewTypes',
        crudURL: '/api/viewType',
        mainClass: CViewType,
      };
    case 'forwarder':
      return {
        componentId: 'forwarders',
        aclId: 'Forwarder',
        entity: 'forwarder',
        fetchURL: '/api/forwarders',
        crudURL: '/api/forwarder',
      };
    case 'transporter':
      return {
        componentId: 'transporters',
        aclId: 'Transporter',
        entity: 'transporter',
        fetchURL: '/api/transporters',
        crudURL: '/api/transporter',
      };
    case 'currency':
      return {
        componentId: 'currencies',
        aclId: 'tsCurrency',
        entity: 'currency',
        fetchURL: '/api/currencies',
        crudURL: '/api/currency',
      };
    case 'contactDataType':
      return {
        componentId: 'contactDataTypes',
        aclId: 'ContactDataType',
        entity: 'contactDataType',
        fetchURL: '/api/contactDataTypes',
        crudURL: '/api/contactDataType',
      };
    //#region WipPart
    case 'qualityCP':
      return {
        componentId: 'qualityCP',
        aclId: 'taQualityCP',
        entity: 'qualityCP',
        fetchURL: '/api/qualityCPs',
        crudURL: '/api/qualityCP',
      };
    case 'customerPurchaseOrder':
      return {
        componentId: 'customerPurchaseOrder',
        aclId: 'taCustomerPurchaseOrder',
        entity: 'customerPurchaseOrder',
        fetchURL: '/api/customerPurchaseOrder',
        crudURL: '/api/customerPurchaseOrde',
      };
    case 'internalPurchaseOrder':
      return {
        componentId: 'internalPurchaseOrder',
        aclId: 'taInternalPurchaseOrder',
        entity: 'internalPurchaseOrder',
        fetchURL: '/api/internalPurchaseOrder',
        crudURL: '/api/internalPurchaseOrde',
      };
    case 'productionSeries':
      return {
        componentId: 'productionSeries',
        aclId: 'taProductionSeries',
        entity: 'productionSeries',
        fetchURL: '/api/productionSeries',
        crudURL: '/api/productionSerie',
      };
    case 'productionBatch':
      return {
        componentId: 'productionBatch',
        aclId: 'taProductionBatch',
        entity: 'productionBatch',
        fetchURL: '/api/productionBatches',
        crudURL: '/api/productionBatche',
      };
    case 'productionJob':
      return {
        componentId: 'productionJob',
        aclId: 'taProductionJob',
        entity: 'productionJob',
        fetchURL: '/api/productionJobs',
        crudURL: '/api/productionJob',
      };
    //#endregion
    case 'kpi':
      return {
        componentId: 'kpiDefinitions',
        aclId: 'KpiDef',
        entity: 'kpi',
        fetchURL: '/api/v2/kpis',
        crudURL: '/api/v2/kpi',
        mainClass: CKPI,
        crudClass: CCreateKPI,
        postEntity: postKPIDefinition,
      };
    case 'layer':
      return {
        componentId: 'layers',
        aclId: 'taLayer',
        entity: 'layer',
        fetchURL: '/api/layers',
        crudURL: '/api/layer',
        mainClass: CLayer,
      };
    case 'ruleset':
      return {
        componentId: 'rulesets',
        aclId: 'tsRuleset',
        entity: 'ruleset',
        fetchURL: '/api/rulesets',
        crudURL: '/api/ruleset',
        mainClass: CRuleset,
      };
    case 'taskType':
      return {
        componentId: 'taskTypes',
        aclId: 'taTaskType',
        entity: 'taskType',
        fetchURL: '/api/taskTypes',
        crudURL: '/api/taskType',
        mainClass: CTaskType,
      };
    case 'purpose':
      return {
        componentId: 'purposes',
        aclId: 'taPurpose',
        entity: 'purpose',
        fetchURL: '/api/purposes',
        crudURL: '/api/purpose',
      };
    case 'channelType':
      return {
        componentId: 'channelTypes',
        aclId: 'ChannelType',
        entity: 'channelType',
        fetchURL: '/api/channelTypes',
        mainClass: CChannelType,
      };
    case 'addressType':
      return {
        componentId: 'addressTypes',
        aclId: 'taAddressType',
        entity: 'addressType',
        fetchURL: '/api/addressTypes',
        crudURL: '/api/addressType',
        mainClass: CAddressType,
      };
    case 'action':
      return {
        componentId: 'actions',
        aclId: 'tsAction',
        entity: 'action',
        fetchURL: '/api/actions',
        crudURL: '/api/action',
      };
    case 'entity':
      return {
        componentId: 'entities',
        aclId: 'tsEntity',
        entity: 'entity',
        fetchURL: '/api/entities',
        crudURL: '/api/entitie',
        mainClass: CEntity,
      };
    case 'distributionList':
      return {
        componentId: 'distributionLists',
        aclId: 'DistList',
        entity: 'distributionList',
        fetchURL: '/api/distributionLists',
        crudURL: '/api/distributionList',
        mainClass: CDistributionList,
      };
    case 'distributionConfiguration':
      return {
        componentId: 'distributionConfiguration',
        aclId: 'DistConf',
        entity: 'distributionConfiguration',
        fetchURL: '/api/distributionConfigurations',
        crudURL: '/api/distributionConfiguration',
        mainClass: CDistributionConfiguration,
      };
    default:
      return {
        componentId: '',
        aclId: '',
        entity: 'any',
        fetchURL: '/api/unknown',
        crudURL: '/api/unknown',
      };
  }
};

const GlobalDataGrid = (props: IGlobalDataGridProps) => {
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false },
  };

  const getColumns = (): GridColDef[] => {
    const globalCells = generateGlobalColumns(props);
    return [globalCells.id(), globalCells.name()];
  };

  return (
    <WrapperDataGrid
      configComponent={props.configComponent}
      dataGridConfig={config}
      columns={getColumns()}
      //Extra data
      inheritedProps={props.inheritedProps}
    />
  );
};

export default GlobalDataGrid;
